import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { CalendarDays, CheckCircle2, PartyPopper, Send, Sparkles, X, XCircle } from 'lucide-react';
import { Button, EmptyState, ErrorState, LoadingState } from '../components/ui';
import { Html } from '../lib/htmlContent';
import { loadMyCustomer } from '../lib/account';
import { pointer, pointerId, queryClass, saveClassRecord, type ParseRecord } from '../lib/parse';
import { correctOptionKey, optionsOf, type TestQuestion } from '../lib/studentTestApi';

type DayEntry = ParseRecord & { Date?: string | { iso?: string }; QuestionPtr?: ParseRecord };
type DailyQuestion = DayEntry & { question: TestQuestion };
type DailyAnswer = ParseRecord & { IsCorrect?: boolean; SubmittedAnswer?: Array<{ SubmittedAnswer?: Array<string | null> }> };

function dayDate(day?: DayEntry) {
  const value = day?.Date;
  const date = value && typeof value === 'object' ? value.iso : value;
  if (!date) return undefined;
  const parsed = new Date(date);
  return Number.isNaN(parsed.getTime()) ? undefined : parsed;
}

function choiceFromAnswer(answer?: DailyAnswer) {
  const submitted = answer?.SubmittedAnswer?.[0]?.SubmittedAnswer;
  if (!Array.isArray(submitted)) return undefined;
  const index = submitted.findIndex((value) => value !== null && value !== undefined && value !== '');
  return index < 0 ? undefined : index + 1;
}

function localAnswerKey(dayId: string) {
  return `ayurguide-v3-qod-answer:${dayId}`;
}

function localDateKey(date: Date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

type StudentQuestionOfDayProps = { popup?: boolean; enabled?: boolean };

export function StudentQuestionOfDay({ popup = false, enabled = true }: StudentQuestionOfDayProps) {
  const [loading, setLoading] = useState(true);
  const [answerCheckComplete, setAnswerCheckComplete] = useState(false);
  const [error, setError] = useState('');
  const [today, setToday] = useState<DailyQuestion>();
  const [previous, setPrevious] = useState<DailyQuestion[]>([]);
  const [selected, setSelected] = useState<number>();
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState('');
  const [popupOpen, setPopupOpen] = useState(false);
  const [celebrating, setCelebrating] = useState(false);

  useEffect(() => {
    let active = true;
    void (async () => {
      setError('');
      setNotice('');
      setAnswerCheckComplete(false);
      try {
        const rows = await queryClass('test_QuestionOfDay', { limit: 100, order: '-Date', include: ['QuestionPtr'] }) as DayEntry[];
        const withQuestions = await Promise.all(rows.map(async (day) => {
          const included = day.QuestionPtr as TestQuestion | undefined;
          if (included?.QuesTitle) return { ...day, question: included } as DailyQuestion;
          const questionId = pointerId(day.QuestionPtr);
          if (!questionId) return undefined;
          const [question] = await queryClass('test_TestQuestions', {
            limit: 1,
            where: (query) => query.equalTo('objectId', questionId),
          }) as TestQuestion[];
          return question?.QuesTitle ? { ...day, question } as DailyQuestion : undefined;
        }));
        const todayKey = localDateKey(new Date());
        const questionDays = withQuestions.filter((day): day is DailyQuestion => Boolean(day?.question && dayDate(day)));
        const todayEntry = questionDays.find((day) => localDateKey(dayDate(day)!) === todayKey);
        const previousEntries = questionDays.filter((day) => {
          const date = dayDate(day);
          return Boolean(date && localDateKey(date) < todayKey);
        }).slice(0, 7);
        if (active) {
          setToday(todayEntry);
          setPrevious(previousEntries);
          setSelected(undefined);
          setSubmitted(false);
        }

        if (todayEntry) {
          try {
            const customer = await loadMyCustomer();
            if (!customer?.objectId) throw new Error('Your learner profile could not be found.');
            const answers = await queryClass('test_QuestionOfDayAnswer', {
              limit: 1,
              order: '-createdAt',
              where: (query) => {
                query.equalTo('CustomerPtr', pointer('elearning_Customer', customer.objectId));
                query.equalTo('QuestionOfDayPtr', pointer('test_QuestionOfDay', todayEntry.objectId));
              },
            }) as DailyAnswer[];
            const answer = answers[0];
            if (active && answer) {
              setSelected(choiceFromAnswer(answer));
              setSubmitted(true);
            } else if (active) {
              const localChoice = Number(localStorage.getItem(localAnswerKey(todayEntry.objectId)) || NaN);
              if (Number.isFinite(localChoice)) setSelected(localChoice);
            }
          } catch {
            if (active) setNotice('We could not check your saved response. Please make sure you are signed in before submitting.');
          }
        }
        if (active) setAnswerCheckComplete(true);
      } catch {
        if (active) setError('The daily question could not load. Check your connection and try again.');
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (popup && enabled && !loading && answerCheckComplete && today && !submitted) setPopupOpen(true);
  }, [popup, enabled, loading, answerCheckComplete, today, submitted]);

  useEffect(() => {
    if (!celebrating) return;
    const timer = window.setTimeout(() => setCelebrating(false), 2800);
    return () => window.clearTimeout(timer);
  }, [celebrating]);

  const submit = async () => {
    if (!today || selected === undefined || saving || submitted) return;
    const correctKey = correctOptionKey(today.question);
    if (correctKey === undefined) {
      setNotice('This question cannot be submitted because its answer key is unavailable.');
      return;
    }
    setSaving(true);
    setNotice('');
    try {
      const customer = await loadMyCustomer();
      if (!customer?.objectId) throw new Error('Your learner account could not be found. Sign in again and retry.');
      const existing = await queryClass('test_QuestionOfDayAnswer', {
        limit: 1,
        where: (query) => {
          query.equalTo('CustomerPtr', pointer('elearning_Customer', customer.objectId));
          query.equalTo('QuestionOfDayPtr', pointer('test_QuestionOfDay', today.objectId));
        },
      }) as DailyAnswer[];
      if (existing[0]) {
        setSelected(choiceFromAnswer(existing[0]));
        setSubmitted(true);
        return;
      }
      const correct = selected === correctKey;
      const submittedAnswer: Array<string | null> = Array.from({ length: 5 }, (_, index) => index + 1 === selected ? String(selected) : null);
      const saved = await saveClassRecord('test_QuestionOfDayAnswer', {
        CustomerPtr: pointer('elearning_Customer', customer.objectId),
        QuestionOfDayPtr: pointer('test_QuestionOfDay', today.objectId),
        IsCorrect: correct,
        SubmittedAnswer: [{
          CorrectAnswer: [String(correctKey)],
          ObtainedMarks: correct ? Number(today.question.Marks ?? 0) : 0,
          SubmittedAnswer: submittedAnswer,
          TestQuestionsPtr: pointer('test_TestQuestions', String(today.question.objectId)),
        }],
      }) as DailyAnswer;
      const savedChoice = choiceFromAnswer(saved) ?? selected;
      setSelected(savedChoice);
      localStorage.setItem(localAnswerKey(today.objectId), String(savedChoice));
      setSubmitted(true);
      setCelebrating(saved.IsCorrect === true && savedChoice === correctKey);
    } catch (reason) {
      setNotice(reason instanceof Error ? reason.message : 'Your answer could not be submitted. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (popup) {
    if (!popupOpen || !today) return null;
    return <QuestionOfDayDialog date={dayDate(today)} onClose={() => setPopupOpen(false)}>
      <QuestionPractice modal today={today} selected={selected} onSelect={setSelected} submitted={submitted} saving={saving} answerChecking={!answerCheckComplete} notice={notice} celebrating={celebrating} onSubmit={() => void submit()} />
    </QuestionOfDayDialog>;
  }

  if (loading) return <LoadingState />;
  if (error) return <div className="utility-page"><ErrorState onRetry={() => window.location.reload()} /></div>;
  return <div className="utility-page">
    <section className="utility-hero"><div className="utility-icon"><CalendarDays size={28} /></div><div><h1>Question of the Day</h1><p>One question a day keeps revision steady.</p></div></section>
    {!today ? <EmptyState icon={CalendarDays} title="No question today" description="A new question will appear here as soon as it is published." /> : (
      <section className="detail-section">
        <h2><Sparkles size={15} /> Today · {dayDate(today)?.toLocaleDateString()}</h2>
        <QuestionPractice today={today} selected={selected} onSelect={setSelected} submitted={submitted} saving={saving} answerChecking={!answerCheckComplete} notice={notice} celebrating={celebrating} onSubmit={() => void submit()} />
      </section>
    )}
    {previous.length > 0 && <section className="detail-section">
      <h2>Previous days</h2>
      <div className="solutions-list">{previous.map((day) => <details className="solution-card" key={day.objectId}>
        <summary className="question-number">{dayDate(day)?.toLocaleDateString() ?? 'Earlier'}</summary>
        <Html html={day.question.QuesTitle} className="question-text" />
        {day.question.SolutionInfo && <details className="solution-notes"><summary>Show explanation</summary><Html html={day.question.SolutionInfo} /></details>}
      </details>)}</div>
    </section>}
  </div>;
}

function QuestionPractice({ today, selected, onSelect, submitted, saving, answerChecking, notice, celebrating, onSubmit, modal = false }: {
  today: DailyQuestion;
  selected?: number;
  onSelect: (key: number) => void;
  submitted: boolean;
  saving: boolean;
  answerChecking: boolean;
  notice: string;
  celebrating: boolean;
  onSubmit: () => void;
  modal?: boolean;
}) {
  const correctKey = correctOptionKey(today.question);
  const correct = submitted && correctKey !== undefined && selected === correctKey;
  return <article className={`qod-practice ${modal ? 'qod-practice-modal' : 'solution-card qod-card'} ${selected !== undefined ? 'has-selection' : ''}`}>
    <Html html={today.question.QuesTitle} className="question-text" />
    <div className="answer-options" aria-label="Answer options">
      {optionsOf(today.question).map(({ key, html }) => {
        const stateClass = !submitted ? (selected === key ? 'selected' : '')
          : correctKey === undefined ? (key === selected ? 'selected-ungraded' : '')
            : key === correctKey ? 'selected-right right' : key === selected ? 'selected-wrong' : '';
        return <button key={key} type="button" className={stateClass} disabled={submitted || saving || answerChecking} aria-pressed={selected === key} onClick={() => onSelect(key)}>
          <span>{String.fromCharCode(64 + key)}</span><Html html={html} />
          {submitted && key === correctKey && <CheckCircle2 size={16} aria-label="Correct answer" />}
          {submitted && correctKey !== undefined && key === selected && key !== correctKey && <XCircle size={16} aria-label="Your answer was incorrect" />}
        </button>;
      })}
    </div>
    {!submitted && <div className="qod-submit-row">
      {(notice || correctKey === undefined) && <p className="form-error" role="alert">{notice || 'This question cannot be submitted because its answer key is unavailable.'}</p>}
      <Button icon={Send} disabled={selected === undefined || saving || answerChecking || correctKey === undefined} onClick={onSubmit}>{saving ? 'Submitting…' : answerChecking ? 'Checking response…' : 'Submit answer'}</Button>
    </div>}
    {submitted && <div className={`qod-answer-reveal ${correct ? 'is-correct' : 'is-incorrect'}`} role="status" aria-live="polite">
      {correct && celebrating && <img className="qod-celebration-art" src="/qod-celebration.gif" alt="" aria-hidden="true" />}
      <strong>{correctKey === undefined ? 'Answer not graded' : correct ? <><PartyPopper size={17} /> Correct. Well done!</> : 'Not quite'}</strong>
      <p>{correctKey === undefined ? 'The answer key is unavailable for this question.' : correct ? `You chose the correct answer, option ${String.fromCharCode(64 + correctKey)}.` : `The correct answer is option ${String.fromCharCode(64 + correctKey)}.`}</p>
      {today.question.SolutionInfo ? <div className="qod-explanation"><span>Explanation</span><Html html={today.question.SolutionInfo} /></div> : <p>No explanation has been added for this question.</p>}
    </div>}
  </article>;
}

function QuestionOfDayDialog({ date, onClose, children }: { date?: Date; onClose: () => void; children: ReactNode }) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog) return;
    dialog.showModal();
    return () => { if (dialog.open) dialog.close(); };
  }, []);

  return createPortal(<dialog ref={dialogRef} className="qod-dialog" aria-labelledby="qod-dialog-title" onCancel={(event) => { event.preventDefault(); onClose(); }} onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <header className="qod-dialog-header">
      <div className="qod-dialog-title-wrap"><span className="qod-dialog-icon"><CalendarDays size={19} /></span><div><h2 id="qod-dialog-title">Question of the Day</h2><p>{date ? date.toLocaleDateString('en-IN', { dateStyle: 'full' }) : 'A short daily practice'}</p></div></div>
      <button className="qod-dialog-close" type="button" onClick={onClose} aria-label="Close Question of the Day"><X size={19} /></button>
    </header>
    <div className="qod-dialog-content">{children}</div>
  </dialog>, document.body);
}
